import { BASE_URL, SITE_NAME, socialProfiles, toAbsoluteUrl } from './seoConfig';

export const buildOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: BASE_URL,
  logo: `${BASE_URL}/logo512.png`,
  sameAs: socialProfiles,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+91-98765-43210',
      contactType: 'customer support',
      areaServed: 'IN',
      availableLanguage: ['en', 'hi'],
    },
  ],
});

export const buildWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/products?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const buildWebPageSchema = ({ title, description, url, image }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: title,
  description,
  url,
  image: image ? toAbsoluteUrl(image) : undefined,
});

export const buildBreadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: toAbsoluteUrl(item.item),
  })),
});

export const buildProductSchema = ({
  id,
  name,
  description,
  images = [],
  sku,
  brand,
  price,
  priceCurrency = 'INR',
  availability = true,
  isPurchasable = true,
  ratingValue,
  reviewCount,
}) => {
  const sanitizedImages = Array.isArray(images) ? images : [images];
  const absoluteImages = sanitizedImages
    .filter(Boolean)
    .map((img) => toAbsoluteUrl(img));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    image: absoluteImages,
    description,
    sku: sku || id,
    brand: {
      '@type': 'Brand',
      name: brand || SITE_NAME,
    },
  };

  // Most of the catalogue is quote-based industrial equipment with no listed
  // price — `price` defaults to 0 rather than being omitted upstream. Never
  // assert a `0` price as fact: without a real, purchasable price there's
  // nothing honest to put in `offers`, so leave it out entirely rather than
  // claim the product costs nothing (Google treats a fabricated/invalid price
  // as spammy structured data, and it's simply untrue for a non-purchasable item).
  if (isPurchasable && typeof price === 'number' && price > 0) {
    schema.offers = {
      '@type': 'Offer',
      url: `${BASE_URL}/products/${id}`,
      priceCurrency,
      price: price.toFixed(2),
      availability: `https://schema.org/${availability ? 'InStock' : 'OutOfStock'}`,
      itemCondition: 'https://schema.org/NewCondition',
    };
  }

  if (ratingValue) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue.toFixed(1),
      reviewCount: reviewCount || 1,
      bestRating: '5',
    };
  }

  // Google's Product rich-result rules require at least one of offers /
  // review / aggregateRating, and quote-only products have no honest price.
  // Keep the product facts for AI/search crawlers but declare it a
  // ProductModel (schema.org subtype of Product: a catalogue/spec entry with
  // no sale listing) so it isn't validated as a purchasable Product.
  if (!schema.offers && !schema.aggregateRating) schema['@type'] = 'ProductModel';

  return schema;
};

export const buildCategorySchema = ({ name, description, url, items = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url,
  hasPart: items.slice(0, 10).map((product) => ({
    '@type': 'Product',
    name: product?.translations?.[0]?.name || product?.title || 'Product',
    image: product?.base_image?.path ? toAbsoluteUrl(product.base_image.path) : undefined,
  })),
});

// CollectionPage + ItemList (mainEntity), for category/subcategory/sub-subcategory
// listing pages — distinct from buildCategorySchema's CollectionPage+hasPart shape,
// which other pages already depend on.
export const buildCollectionItemListSchema = ({ name, description, url, items = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url: url ? toAbsoluteUrl(url) : undefined,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: toAbsoluteUrl(item.url),
    })),
  },
});

// TechArticle for case studies — they're technical write-ups (challenge/
// solution/products used), which schema.org's TechArticle type fits better
// than a generic Article.
export const buildTechArticleSchema = ({
  title,
  description,
  url,
  image,
  datePublished,
  dateModified,
}) => ({
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: title,
  description,
  url: url ? toAbsoluteUrl(url) : undefined,
  image: image ? toAbsoluteUrl(image) : undefined,
  datePublished,
  dateModified: dateModified || datePublished,
  author: {
    '@type': 'Organization',
    name: SITE_NAME,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo512.png`,
    },
  },
});

export const buildSearchResultsSchema = ({ query, results = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'SearchResultsPage',
  name: `Search results for ${query}`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/products?search=${encodeURIComponent(query)}`,
    'query-input': 'required name=search_term_string',
  },
  about: results.slice(0, 10).map((result) => ({
    '@type': 'Thing',
    name: result?.translations?.[0]?.name || result?.title || 'Product',
  })),
});

export const buildCartSchema = ({ items = [], currency = 'INR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Shopping Cart',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Product',
      name: item.name,
      image: item.image ? toAbsoluteUrl(item.image) : undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: currency,
        price: item.price,
        availability: 'https://schema.org/InStock',
      },
    },
  })),
});

export const buildFAQSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export const buildContactSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: SITE_NAME,
  image: `${BASE_URL}/logo512.png`,
  url: BASE_URL,
  telephone: '+91-98765-43210',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '123 Market Street',
    addressLocality: 'Mumbai',
    addressRegion: 'MH',
    postalCode: '400001',
    addressCountry: 'IN',
  },
  sameAs: socialProfiles,
});

